import { formatDateTime, t } from "@hakili/i18n";
import {
  BANK_BG_NAV,
  BANK_BG_NAV_AVATAR,
  BANK_BG_PAGE,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_LH_BODY,
  BANK_NAV_ACTIVE,
  BANK_NAV_HOVER,
  BANK_RADIUS_MD,
  BANK_TEXT,
  BANK_TEXT_NAV,
  HakiliMark,
  PageColumn,
  RAIL_NAV,
  RailAvatarMenu,
  classes,
  useDemandeFonctionnalite,
} from "@hakili/ui";
import { PolicyMissingBanner } from "./policy-missing-banner.js";
import { Bell, FolderOpen, House, Kanban, Lightbulb, Scale, Users, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from "react";

/**
 * Round `L-0`: **the bank's shell, to the drawing (`hakili-bank.jsx`, `NavRail`).**
 *
 * The rail is the persona's, from the ROLE (never chosen): the analyst's four
 * entries and the chef's seven, in the drawing's order and words. An entry
 * whose screen is not built is greyed with its round in its title (R81); the
 * initials come from `/me`; « Une idée ? » is live by the product-feedback
 * door, which admits any authenticated principal (`@SelfService()`, 05
 * 2.16.1: « every request from every app »); the bell is greyed with its
 * module; « Se déconnecter » is in the shell (6.2). The drawing's « Vue » pill
 * is its demonstration device and is not built.
 */
export type BankScreen =
  "pipeline" | "dossiers" | "workspace" | "decisions" | "decision" | "comite" | "politique" | "equipe";

export interface RailEntry {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  /** The built screen it opens. */
  readonly screen: BankScreen;
}

/** `F-1c`, RAIL-FOOT: a 44 x 44 target (the bell, the idea); the account's wrapper is the same size. */
const FOOT_ICON =
  "relative grid h-11 w-11 place-items-center rounded-[var(--hk-r-md)] transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:-outline-offset-[3px]";

const icon = (Icon: typeof House): ReactNode => <Icon className="h-5 w-5" aria-hidden="true" />;

/**
 * `F-2`, the designer's A-2 review: **one entry, one glyph, whoever reads it.** Pipeline was a house for the analyst and a
 * folder for the chef, and the folder is « Dossiers »'s. The glyphs are D2's own (`railItems`: decisions home, pipeline
 * pipeline, dossiers folder, team, politique scale); the icon set itself is not ruled, so the nearest lucide drawing of each.
 */
export function railFor(persona: "analyste" | "chef"): readonly RailEntry[] {
  // `F-1`, DX-B-015, D-1019-2: **only built entries are drawn.** « Comité » and « Pilotage » (no screen yet) are
  // not greyed with their round beside them; they are absent until their screens exist.
  if (persona === "chef") {
    return [
      { id: "decisions", label: t("bank.nav.decisions"), icon: icon(House), screen: "decisions" },
      { id: "pipeline", label: t("bank.nav.pipeline"), icon: icon(Kanban), screen: "pipeline" },
      { id: "equipe", label: t("bank.nav.equipe"), icon: icon(Users), screen: "equipe" },
      { id: "politique", label: t("bank.nav.politique"), icon: icon(Scale), screen: "politique" },
    ];
  }
  return [
    { id: "pipeline", label: t("bank.nav.pipeline"), icon: icon(Kanban), screen: "pipeline" },
    { id: "dossiers", label: t("bank.nav.dossiers"), icon: icon(FolderOpen), screen: "dossiers" },
  ];
}

export const initialsOf = (fullName: string): string =>
  fullName
    .split(/\s+/u)
    .filter((part) => part !== "")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

export function BankShell(props: {
  readonly persona: "analyste" | "chef";
  readonly fullName: string;
  /** `F-1c`: the bank's name as the session answers it (`/me`'s `organisation.name`); absent, the menu's identity line says no bank. */
  readonly organisation?: string;
  readonly screen: BankScreen;
  readonly onScreen: (screen: BankScreen) => void;
  readonly deconnecter: () => void;
  /**
   * `U-4`, `M3` (`U-3`'s booking 7, `K-1`'s shape): the bell , the rows the door answers, the
   * unread count derived from them, and her own act of reading one. Absent where it is not wired,
   * and the bell then stays greyed with its reason exactly as it was.
   */
  readonly notifications?: {
    readonly rows: readonly {
      readonly id: string;
      readonly title: string;
      readonly body: string;
      readonly linkPath: string | null;
      readonly readAt: string | null;
      /** The date the row shows (D2 frame 147), as the door serves it. */
      readonly createdAt: string;
    }[];
    /** The number the door served (the list's answer, or the mark-all answer): the shell counts nothing. */
    readonly unread: number;
    readonly onRead: (id: string) => void;
    /** `MARK-ALL-READ`: « Tout marquer comme lu », by the one door; the rows follow what the server then answers. */
    readonly onReadAll: () => void;
    readonly onOpen: (linkPath: string | null) => void;
  };
  /**
   * `POLICY-FIRST` Screen B: present while the bank's policy is missing (the door answered `policy: null`). The banner shows above the
   * title of every screen but the Politique; its button is the way there.
   */
  readonly policyMissing?: { readonly onGo: () => void };
  readonly children: ReactNode;
  /** `F-2`: the underwriter's bay, beside the work column (stacked under 1 100 px). Absent on a list screen, which has none. */
  readonly bay?: ReactNode;
}): React.JSX.Element {
  const { ouvrir } = useDemandeFonctionnalite();
  const [bellOpen, setBellOpen] = useState(false);
  const bell = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  // `NOTIFS-D2` (D-1028-1): focus lands on the first row, Escape closes and gives focus back to the bell, and a press anywhere
  // else (the bell itself excepted: it toggles) closes it.
  useEffect(() => {
    if (!bellOpen) return undefined;
    // D2 shows no tooltip under the open panel, and focus goes INTO it: the first row, or the panel itself when it is empty.
    setTip(null);
    // D2 focuses the panel's first focusable in document order: « Tout marquer comme lu » when it is drawn, else the first row.
    (
      panel.current?.querySelector<HTMLElement>('[data-focus="first"], [data-notif="row"]') ?? panel.current
    )?.focus();
    const away = (event: PointerEvent): void => {
      const inside = (node: Node | null): boolean =>
        node !== null && (panel.current?.contains(node) ?? false);
      const onBell = event.target instanceof Node && (bell.current?.contains(event.target) ?? false);
      if (event.target instanceof Node && !inside(event.target) && !onBell) setBellOpen(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setBellOpen(false);
        bell.current?.focus();
      }
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [bellOpen]);
  // `F-1`, the designer's Change 1: under 1 100 px the rail shows icons only, and each entry's name appears as a tooltip on
  // hover AND on keyboard focus. It is `fixed` (the aside is `overflow-hidden` and would clip anything inside it).
  // `F-1c`: the foot's names show at EVERY width (`always`); the entries' only under 1 100 px, where they have lost their words.
  const [tip, setTip] = useState<{ label: string; top: number; left: number; always: boolean } | null>(null);
  const show = (event: SyntheticEvent<HTMLElement>, label: string, always = false): void => {
    const box = event.currentTarget.getBoundingClientRect();
    setTip({ label, top: box.top + box.height / 2, left: box.right + 8, always });
  };
  const footTip = (label: string, suppressed = false) =>
    ({
      onFocus: (event: SyntheticEvent<HTMLElement>) => {
        if (!suppressed) show(event, label, true);
      },
      onBlur: () => {
        setTip(null);
      },
      onMouseEnter: (event: SyntheticEvent<HTMLElement>) => {
        if (!suppressed) show(event, label, true);
      },
      onMouseLeave: () => {
        setTip(null);
      },
    }) as const;
  const unread = props.notifications?.unread ?? 0;
  const entries = railFor(props.persona);
  const active = (entry: RailEntry): boolean =>
    entry.screen === props.screen ||
    // D2 `app.js:39`: an opened file reads « pipeline » in the rail, whichever list it was opened from.
    (props.screen === "workspace" && entry.id === "pipeline") ||
    (props.screen === "decision" && entry.id === "decisions") ||
    (props.screen === "comite" && entry.id === "decisions");
  return (
    <div
      className={classes("flex h-screen antialiased", BANK_BG_PAGE, BANK_TEXT, BANK_FS_READ, BANK_LH_BODY)}
      data-testid="bank-shell"
    >
      {/* `F-1`: the dark rail (D2): `--hk-nav`, 76 px (64 under 1 100), the mark on a white tile, entries 13 px.
          The bottom padding was the room the build stamp needed; the stamp is dropped (D-1026-1), so the foot rests 16 px above the bottom, as D2 draws it (`AVATAR-D2`, A4). */}
      <aside
        aria-label={t("bank.nav.pipeline")}
        className={classes(
          "sticky top-0 z-20 flex h-screen w-[var(--hk-rail)] shrink-0 flex-col items-center overflow-y-auto overflow-x-hidden pb-4 pt-4",
          BANK_BG_NAV,
          BANK_TEXT_NAV,
        )}
        data-testid="bank-rail"
      >
        <button
          type="button"
          className={classes("mb-4 grid shrink-0 bg-white p-1.5", BANK_RADIUS_MD)}
          onClick={() => {
            props.onScreen(props.persona === "chef" ? "decisions" : "pipeline");
          }}
          aria-label="Hakili"
          data-testid="nav-marque"
        >
          <HakiliMark size={30} />
        </button>
        <nav className={classes(RAIL_NAV, "items-center gap-1.5")}>
          {entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-current={active(entry) ? "page" : undefined}
              aria-label={entry.label}
              title={entry.label}
              onFocus={(event) => {
                show(event, entry.label);
              }}
              onBlur={() => {
                setTip(null);
              }}
              onMouseEnter={(event) => {
                show(event, entry.label);
              }}
              onMouseLeave={() => {
                setTip(null);
              }}
              onClick={() => {
                props.onScreen(entry.screen);
              }}
              className={classes(
                "flex w-[calc(var(--hk-rail)-14px)] shrink-0 flex-col items-center gap-[3px] py-[9px] font-medium transition-colors",
                BANK_RADIUS_MD,
                BANK_FS_META,
                active(entry)
                  ? classes(BANK_NAV_ACTIVE, "text-white")
                  : classes(BANK_TEXT_NAV, BANK_NAV_HOVER),
              )}
              data-kit="rail-item"
              data-testid={`nav-${entry.id}`}
            >
              {entry.icon}
              {/* The word is not drawn under 1 100 px (D2: the rail is 64 px there); the button keeps its name (aria-label). */}
              <span className="text-center leading-tight max-[1099px]:hidden">{entry.label}</span>
            </button>
          ))}
        </nav>
        {/* `F-1c`, RAIL-FOOT (the founder's order, the designer's paste): bell, idea, account, ONE ABOVE THE OTHER, under a hairline;
            icon-only at every width, each named by a tooltip on hover and on focus. « Profil » is not in the menu: its screen
            is not built (`/profil` is in the route table, no screen answers it), and a menu never says « à construire » (D-1019-2). */}
        <div className="mt-auto flex w-[calc(var(--hk-rail)-24px)] shrink-0 flex-col items-center gap-2 border-t border-[color:var(--hk-nav-line)] pt-3">
          {/* `F-1`, DX-B-015: with no notifications door there is no bell, not a greyed one. */}
          {props.notifications === undefined ? null : (
            /* `U-4`, `M3`: the bell, live. The count is DERIVED from the rows the door answered ,
               never a counter kept somewhere , and a row is read by her own act below. */
            <button
              type="button"
              onClick={() => {
                setBellOpen((open) => !open);
              }}
              ref={bell}
              aria-haspopup="dialog"
              aria-expanded={bellOpen}
              aria-label={
                unread === 0
                  ? t("bank.nav.notifications")
                  : `${t("bank.nav.notifications")}, ${
                      unread === 1
                        ? t("bank.nav.notificationsUneNonLue")
                        : t("bank.nav.notificationsNonLues").replace("{n}", String(unread))
                    }`
              }
              className={classes(FOOT_ICON, BANK_TEXT_NAV, BANK_NAV_HOVER)}
              data-kit="rail-item"
              data-testid="nav-notifications"
              {...footTip(t("bank.nav.notifications"), bellOpen)}
            >
              <Bell className="h-5 w-5" aria-hidden="true" />
              {unread === 0 ? null : (
                <span
                  className={classes(
                    "absolute right-[2px] top-[3px] h-[18px] min-w-[18px] rounded-full bg-[color:var(--hk-count)] px-[5px] text-center font-bold leading-[18px] text-white ring-2 ring-[color:var(--hk-nav)]",
                    BANK_FS_META,
                  )}
                  data-testid="nav-notifications-compte"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              ouvrir(`bank:${props.screen}:une-idee`);
            }}
            aria-label={t("bank.nav.uneIdee")}
            className={classes(FOOT_ICON, BANK_TEXT_NAV, BANK_NAV_HOVER)}
            data-kit="rail-item"
            data-testid="nav-une-idee"
            {...footTip(t("bank.nav.uneIdee"))}
          >
            <Lightbulb className="h-5 w-5" aria-hidden="true" />
          </button>
          <div
            className="grid h-11 w-11 place-items-center"
            {...footTip(t("bank.nav.compte"))}
            onFocus={(event) => {
              // `AVATAR-D2`, A6: focus moves INTO the opened menu; its tooltip belongs to the avatar, not to what the menu holds.
              if (event.target instanceof Element && event.target.closest('[role="menu"]') !== null) {
                setTip(null);
                return;
              }
              show(event, t("bank.nav.compte"), true);
            }}
            onClick={() => {
              setTip(null);
            }}
          >
            <RailAvatarMenu
              initials={initialsOf(props.fullName)}
              name={props.fullName}
              tone={classes(BANK_BG_NAV_AVATAR, "text-white")}
              testId="bank-initiales"
              identity={{
                name: props.fullName,
                role: t(props.persona === "chef" ? "bank.persona.chef" : "bank.persona.analyste"),
                tile: initialsOf(props.fullName),
                ...(props.organisation === undefined ? {} : { organisation: props.organisation }),
              }}
              wide
              focusFirst
              items={[
                {
                  label: t("bank.session.deconnexion"),
                  testId: "bank-logout",
                  onSelect: props.deconnecter,
                  icon: <X className="h-[18px] w-[18px]" aria-hidden="true" />,
                },
              ]}
            />
          </div>
        </div>
      </aside>
      {tip === null ? null : (
        <div
          role="tooltip"
          className={classes(
            "pointer-events-none fixed z-50 -translate-y-1/2 rounded-[8px] px-[10px] py-[6px] text-[13px] font-medium",
            tip.always ? "block" : "hidden max-[1099px]:block",
          )}
          style={{ top: tip.top, left: tip.left, background: "var(--hk-nav)", color: "var(--hk-surface)" }}
        >
          {tip.label}
        </div>
      )}
      {props.notifications === undefined || !bellOpen ? null : (
        <div
          ref={panel}
          role="dialog"
          tabIndex={-1}
          aria-label={t("bank.nav.notifications")}
          data-notif="panel"
          data-testid="bank-notifications-panneau"
        >
          <div data-part="head">
            <h2>{t("bank.nav.notifications")}</h2>
            <span aria-hidden="true" className="flex-1" />
            {unread === 0 ? null : (
              <button
                type="button"
                onClick={() => {
                  props.notifications?.onReadAll();
                }}
                data-part="tout-lire"
                data-focus="first"
                data-testid="bank-notifications-tout-lire"
              >
                {t("bank.nav.notificationsToutLire")}
              </button>
            )}
          </div>
          {props.notifications.rows.length === 0 ? (
            <div data-part="empty" data-testid="bank-notifications-vide">
              <Bell className="h-7 w-7" strokeWidth={1.5} aria-hidden="true" />
              <p>
                <b>{t("bank.nav.notificationsAucune")}</b>
              </p>
              <p>{t("bank.nav.notificationsAucuneAide")}</p>
            </div>
          ) : (
            <ul data-part="list">
              {props.notifications.rows.map((row) => {
                const unreadRow = row.readAt === null;
                return (
                  <li key={row.id} data-testid={`bank-notification-${row.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        props.notifications?.onRead(row.id);
                        props.notifications?.onOpen(row.linkPath);
                        setBellOpen(false);
                      }}
                      data-notif="row"
                      /* The row says whether it has been read , the same fact the dot and the weight draw. */
                      data-lue={unreadRow ? "non" : "oui"}
                      data-testid={`bank-notification-lire-${row.id}`}
                    >
                      <span data-part="dot" data-filled={unreadRow ? "true" : "false"} aria-hidden="true" />
                      <span data-part="body">
                        <span data-part="title" data-bold={unreadRow ? "true" : "false"}>
                          {row.title}
                          {unreadRow ? (
                            <span data-part="chip">{t("bank.nav.notificationNonLue")}</span>
                          ) : null}
                        </span>
                        <span data-part="text">{row.body}</span>
                        <time data-part="date" dateTime={row.createdAt}>
                          {formatDateTime(new Date(row.createdAt))}
                        </time>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <main className="min-w-0 flex-1 overflow-y-auto">
        <PageColumn
          variant="bank"
          {...(props.bay === undefined ? {} : { bay: props.bay, bayLabel: t("bank.bay.titre") })}
        >
          {props.policyMissing === undefined || props.screen === "politique" ? null : (
            <PolicyMissingBanner persona={props.persona} onGo={props.policyMissing.onGo} />
          )}
          {props.children}
        </PageColumn>
      </main>
    </div>
  );
}
